import { Environment } from '../constants/environments';
import { getFacebookTrackingParams } from '../utils/fbclid';
import { logger } from '../utils/logger';

type UserData = {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  fbp?: string | null;
  fbc?: string | null;
  client_ip_address?: string;
  client_user_agent?: string;
  external_id?: string;
};

type CustomData = {
  currency?: string;
  value?: number;
  content_name?: string;
  content_ids?: string[];
  content_type?: string;
  contents?: Array<object>;
};

type MetaEvent = {
  event_name: string;
  event_time: number;
  event_id?: string;
  event_source_url: string;
  action_source: string;
  user_data: UserData;
  custom_data?: CustomData;
};

class MetaConversionsAPI {
  private apiUrl: string;

  constructor() {
    this.apiUrl = Environment.API_META_CONVERSIONS_URL || '';

    if (!this.apiUrl) {
      logger.warn('Meta Conversions API: API URL is not configured');
    } else {
      logger.info('Meta Conversions API initialized');
    }
  }

  /**
   * Generate a unique event ID for deduplication with Meta Pixel
   */
  private generateEventId(): string {
    return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Build user data with Facebook tracking parameters
   */
  private buildUserData(userData?: Partial<UserData>): UserData {
    const facebookParams = getFacebookTrackingParams();

    return {
      ...userData,
      fbp : facebookParams.fbp,
      fbc : facebookParams.fbc,
      client_user_agent : navigator.userAgent,
      // Note: client_ip_address should ideally be set server-side
      // The backend Lambda will handle this if not provided
    };
  }

  /**
   * Send event to Meta Conversions API
   */
  private async sendEvent(event: MetaEvent): Promise<boolean> {
    if (!this.apiUrl) {
      // Silently skip if not configured - already warned in constructor
      return false;
    }

    try {
      const response = await fetch(this.apiUrl, {
        method : 'POST',
        headers : {
          'Content-Type' : 'application/json',
        },
        body : JSON.stringify(event),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ message : 'Failed to parse error response' }));
        logger.error('Meta Conversions API error', {
          status : response.status,
          statusText : response.statusText,
          body : errorBody,
          event : event.event_name,
          event_id : event.event_id,
        });
        return false;
      }

      const data = await response.json();
      logger.debug('Meta Conversions API success', {
        event : event.event_name,
        event_id : event.event_id,
        response : data,
      });
      return true;
    } catch (error) {
      logger.error('Meta Conversions API request failed', {
        error : error instanceof Error ? error.message : String(error),
        event : event.event_name,
      });
      return false;
    }
  }

  /**
   * Track PageView event
   */
  async trackPageView(userData?: Partial<UserData>): Promise<void> {
    const eventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : 'PageView',
      event_time : Math.floor(Date.now() / 1000),
      event_id : eventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
    };

    await this.sendEvent(event);
  }

  /**
   * Track AddToCart event
   */
  async trackAddToCart(
    value: number,
    contents: Array<object>,
    currency: string,
    userData?: Partial<UserData>
  ): Promise<void> {
    const eventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : 'AddToCart',
      event_time : Math.floor(Date.now() / 1000),
      event_id : eventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
      custom_data : {
        currency,
        value,
        contents,
      },
    };

    await this.sendEvent(event);
  }

  /**
   * Track Purchase event
   */
  async trackPurchase(
    totalValue: number,
    contents: Array<object>,
    eventId: string,
    currency: string,
    userData?: Partial<UserData>
  ): Promise<void> {
    const trackingEventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : 'Purchase',
      event_time : Math.floor(Date.now() / 1000),
      event_id : trackingEventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
      custom_data : {
        currency,
        value : totalValue,
        contents,
        content_ids : [eventId],
        content_type : 'product',
      },
    };

    await this.sendEvent(event);
  }

  /**
   * Track InitiateCheckout event
   */
  async trackInitiateCheckout(
    value: number,
    contents: Array<object>,
    currency: string,
    userData?: Partial<UserData>
  ): Promise<void> {
    const eventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : 'InitiateCheckout',
      event_time : Math.floor(Date.now() / 1000),
      event_id : eventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
      custom_data : {
        currency,
        value,
        contents,
      },
    };

    await this.sendEvent(event);
  }

  /**
   * Track AddPaymentInfo event
   */
  async trackAddPaymentInfo(
    value: number,
    contents: Array<object>,
    currency: string,
    userData?: Partial<UserData>
  ): Promise<void> {
    const eventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : 'AddPaymentInfo',
      event_time : Math.floor(Date.now() / 1000),
      event_id : eventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
      custom_data : {
        currency,
        value,
        contents,
      },
    };

    await this.sendEvent(event);
  }

  /**
   * Track ViewContent event
   */
  async trackViewContent(
    contentName: string,
    contentIds: string[],
    value?: number,
    currency?: string,
    userData?: Partial<UserData>
  ): Promise<void> {
    const eventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : 'ViewContent',
      event_time : Math.floor(Date.now() / 1000),
      event_id : eventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
      custom_data : {
        content_name : contentName,
        content_ids : contentIds,
        content_type : 'product',
        ...(value && { value }),
        ...(currency && { currency }),
      },
    };

    await this.sendEvent(event);
  }

  /**
   * Track custom event
   */
  async trackCustomEvent(
    eventName: string,
    customData?: CustomData,
    userData?: Partial<UserData>
  ): Promise<void> {
    const eventId = this.generateEventId();

    const event: MetaEvent = {
      event_name : eventName,
      event_time : Math.floor(Date.now() / 1000),
      event_id : eventId,
      event_source_url : window.location.href,
      action_source : 'website',
      user_data : this.buildUserData(userData),
      ...(customData && { custom_data : customData }),
    };

    await this.sendEvent(event);
  }
}

export default new MetaConversionsAPI();
